'use client'

import * as React from 'react'

import { motion, type Transition } from 'framer-motion'
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { Button } from '@/components/ui/button'
import AssetPicture from '@/components/ui/AssetPicture'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

type PropType = {
  slides: number[]
  options?: EmblaOptionsType
  images?: string[]
  /* Per-slide labels: used for dot buttons, alt text, and the slide caption overlay. */
  labels?: string[]
}

type EmblaControls = {
  selectedIndex: number
  scrollSnaps: number[]
  prevDisabled: boolean
  nextDisabled: boolean
  onDotClick: (index: number) => void
  onPrev: () => void
  onNext: () => void
}

type DotButtonProps = {
  selected?: boolean
  label: string
  onClick: () => void
}

const transition: Transition = {
  stiffness: 240,
  damping: 24,
  mass: 1,
}

const useEmblaControls = (emblaApi: EmblaCarouselType | undefined): EmblaControls => {
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([])
  const [prevDisabled, setPrevDisabled] = React.useState(true)
  const [nextDisabled, setNextDisabled] = React.useState(true)

  const onDotClick = React.useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi])

  const onPrev = React.useCallback(() => emblaApi?.scrollPrev(), [emblaApi])
  const onNext = React.useCallback(() => emblaApi?.scrollNext(), [emblaApi])

  const updateSelectionState = (api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap())
    setPrevDisabled(!api.canScrollPrev())
    setNextDisabled(!api.canScrollNext())
  }

  const onInit = React.useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList())
    updateSelectionState(api)
  }, [])

  const onSelect = React.useCallback((api: EmblaCarouselType) => {
    updateSelectionState(api)
  }, [])

  React.useEffect(() => {
    if (!emblaApi) return

    // Embla is already initialized when this effect runs; reading its state
    // once here is the only way to seed the UI (no event replays it).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    onInit(emblaApi)
    emblaApi.on('reInit', onInit).on('select', onSelect)

    return () => {
      emblaApi.off('reInit', onInit).off('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  return {
    selectedIndex,
    scrollSnaps,
    prevDisabled,
    nextDisabled,
    onDotClick,
    onPrev,
    onNext,
  }
}

function MotionCarousel(props: PropType) {
  const { slides, options, images, labels } = props
  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const { selectedIndex, scrollSnaps, prevDisabled, nextDisabled, onDotClick, onPrev, onNext } =
    useEmblaControls(emblaApi)

  return (
    <div className="w-full space-y-4 [--slide-height:14rem] [--slide-size:70%] [--slide-spacing:1rem] sm:[--slide-height:18rem] sm:[--slide-size:55%] sm:[--slide-spacing:1.5rem] md:[--slide-height:24rem] md:[--slide-size:42%]">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex touch-pan-y touch-pinch-zoom">
          {slides.map((index) => {
            const isActive = index === selectedIndex

            return (
              <motion.div
                key={index}
                className="me-(--slide-spacing) flex h-(--slide-height) min-w-0 flex-none basis-(--slide-size)"
              >
                <motion.div
                  className="relative flex size-full items-center justify-center overflow-hidden rounded-xl select-none"
                  initial={false}
                  animate={{
                    scale: isActive ? 1 : 0.9,
                  }}
                  transition={transition}
                >
                  {images?.[index] ? (
                    <>
                      {/* AssetPicture serves the AVIF/WebP variants when given a
                          manifest key; absolute URLs pass through unchanged.
                          Below the fold, so lazy. */}
                      <AssetPicture
                        src={images[index]}
                        alt={labels?.[index] ?? `Slide ${index + 1}`}
                        className="size-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                      {labels?.[index] && (
                        <motion.p
                          className="absolute inset-x-0 bottom-0 px-4 pb-3 pt-8 text-sm font-bold text-white [background:linear-gradient(to_top,rgba(0,0,0,0.7),transparent)]"
                          initial={false}
                          animate={{ opacity: isActive ? 1 : 0 }}
                          transition={transition}
                        >
                          {labels[index]}
                        </motion.p>
                      )}
                    </>
                  ) : (
                    index + 1
                  )}
                </motion.div>
              </motion.div>
            )
          })}
        </div>
      </div>

      <div className="flex justify-between">
        <Button size="icon" onClick={onPrev} disabled={prevDisabled} aria-label="Previous slide">
          <ChevronLeftIcon className="size-5 rtl:rotate-180" />
        </Button>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              label={labels?.[index] ?? `Slide ${index + 1}`}
              selected={index === selectedIndex}
              onClick={() => onDotClick(index)}
            />
          ))}
        </div>

        <Button size="icon" onClick={onNext} disabled={nextDisabled} aria-label="Next slide">
          <ChevronRightIcon className="size-5 rtl:rotate-180" />
        </Button>
      </div>
    </div>
  )
}

function DotButton({ selected = false, label, onClick }: DotButtonProps) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      layout
      initial={false}
      className="bg-primary text-primary-foreground flex cursor-pointer items-center justify-center overflow-hidden rounded-full border-none text-sm select-none"
      animate={{
        width: selected ? 'auto' : 12,
        height: selected ? 28 : 12,
      }}
      transition={transition}
    >
      <motion.span
        layout
        initial={false}
        className="block px-3 py-1 whitespace-nowrap"
        animate={{
          opacity: selected ? 1 : 0,
          scale: selected ? 1 : 0,
          filter: selected ? 'blur(0px)' : 'blur(4px)',
        }}
        transition={transition}
      >
        {label}
      </motion.span>
    </motion.button>
  )
}

export { MotionCarousel }
